import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../services/authService';
import { setUserData } from '../features/user/userSlice';
import { clearRedirectPath, closeModals } from '../features/modals/modalSlice';

const extractApiError = (error) => {
  const data = error?.response?.data;
  return (
    data?.message ||
    data?.error?.message ||
    data?.error ||
    error?.message ||
    'Login failed'
  );
};

export const useAuth = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordSignUp, setShowPasswordSignUp] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [passwordResetModal, setPasswordResetModal] = useState(false);
  const [updatePasswordModal, setUpdatePasswordModal] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const redirectPath = useSelector((state) => state.modal.redirectPath);
  const { showSignIn, showSignUp } = useSelector((state) => state.modal);

  const isLoggedIn = !!user?.user_id;

  // Check for verification messages on mount
  useEffect(() => {
    const verified = localStorage.getItem('verified');
    const error = localStorage.getItem('verify-error');

    if (verified || error) {
      setTimeout(() => {
        if (verified) {
          setMessage('✅ Your account has been successfully verified!');
          localStorage.removeItem('verified');
        } else {
          setMessage(`❌ ${error}`);
          localStorage.removeItem('verify-error');
        }
        setShowModal(true);
      }, 1500);
    }
  }, []);

  const handleSignIn = async (values, helpers) => {
    const { setSubmitting, setStatus } = helpers || {};
    try {
      const email = (values?.workEmail || '').trim().toLowerCase();
      const password = values?.password || '';

      const response = await authService.signIn(email, password);

      // Store authentication data
      const userData = authService.storeAuthData(
        response.token,
        response.user_id,
        response.work_email
      );

      // Update Redux state
      dispatch(setUserData(userData));

      setStatus?.({
        success: true,
        message: response.message || 'Logged in successfully.',
      });

      // SHORTER SUCCESS TOAST
      toast.success('Welcome back! Logged in successfully.', {
        autoClose: 800,
        hideProgressBar: true,
      });

      dispatch(closeModals());

      // Redirect if needed
      if (redirectPath) {
        navigate(redirectPath);
        dispatch(clearRedirectPath());
      }
    } catch (error) {
      console.error('Login failed:', error?.response?.data || error?.message);
      const errMsg = extractApiError(error);

      setStatus?.({
        success: false,
        message: errMsg,
      });
      toast.error(errMsg);
    } finally {
      setSubmitting?.(false);
    }
  };

  const handleSignUp = async (values, helpers) => {
    const { setSubmitting } = helpers || {};
    try {
      setSubmitError('');

      const workEmail = (values?.workEmail || '').trim().toLowerCase();
      const personalEmail = (values?.personalEmail || '').trim().toLowerCase();
      const password = values?.password || '';

      await authService.signUp(workEmail, personalEmail, password);

      dispatch(closeModals());
      setShowSuccessModal(true);
      toast.success('Account created! Please check your email to verify.');
    } catch (error) {
      const apiMessage = extractApiError(error);
      setSubmitError(apiMessage);
      toast.error(apiMessage);
    } finally {
      setSubmitting?.(false);
    }
  };

  const handleSignOut = () => {
    authService.clearAuthData();
    dispatch(setUserData({}));
    setProfileDropdownOpen(false);
    
    // Clear cached engagements on logout
    localStorage.removeItem('userEngagements');
    
    // SHORTER SUCCESS TOAST ON LOGOUT TOO (optional)
    toast.success("You've been logged out successfully.", {
      autoClose: 800,
      hideProgressBar: true,
    });
    navigate('/');
  };

  const handleShowVerificationModal = () => {
    dispatch(closeModals());
    setShowSuccessModal(true);
  };

  return {
    // State
    showPassword,
    setShowPassword,
    showPasswordSignUp,
    setShowPasswordSignUp,
    submitError,
    showModal,
    setShowModal,
    message,
    showSuccessModal,
    setShowSuccessModal,
    profileDropdownOpen,
    setProfileDropdownOpen,
    isLoggedIn,
    user,
    showSignIn,
    showSignUp,
    passwordResetModal,
    setPasswordResetModal,
    updatePasswordModal,
    setUpdatePasswordModal,
    resetToken,
    setResetToken,

    // Actions
    handleSignIn,
    handleSignUp,
    handleSignOut,
    handleShowVerificationModal,
  };
};